"use client"

import { useState } from "react"
import { motion } from "framer-motion"
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  Send,
  ChevronRight,
  Shield,
  Layers,
  Building2,
  HardHat,
  CheckCircle2,
  Menu,
  X,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useToast } from "@/hooks/use-toast"

const services = [
  {
    icon: Layers,
    title: "Plasterboard Installation",
    description:
      "Expert plasterboard fixing and finishing for walls and ceilings, delivering flawless surfaces every time.",
  },
  {
    icon: Shield,
    title: "Insulation Solutions",
    description:
      "Thermal and acoustic insulation systems designed for maximum energy efficiency and comfort.",
  },
  {
    icon: Building2,
    title: "AAC Installation",
    description:
      "Autoclaved Aerated Concrete blockwork and panel systems for lightweight, high-performance construction.",
  },
]

const contactInfo = [
  {
    icon: Phone,
    label: "Phone",
    value: "+61 2 8000 1234",
    href: "tel:+61280001234",
  },
  {
    icon: Mail,
    label: "Email",
    value: "partnerships@drclad.com.au",
    href: "mailto:partnerships@drclad.com.au",
  },
  {
    icon: MapPin,
    label: "Office Address",
    value: "42 Industrial Boulevard, Parramatta NSW 2150, Australia",
    href: "#",
  },
  {
    icon: Clock,
    label: "Business Hours",
    value: "Mon – Fri: 7:00 AM – 5:00 PM | Sat: 8:00 AM – 12:00 PM",
    href: "#",
  },
]

const subjectOptions = [
  "New Project Inquiry",
  "Partnership Opportunity",
  "Quote Request",
  "General Inquiry",
  "Technical Support",
  "Other",
]

export default function Home() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    subject: "",
    projectDetails: "",
  })
  const { toast } = useToast()

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }))
  }

  const handleSubjectChange = (value: string) => {
    setFormData((prev) => ({ ...prev, subject: value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (
      !formData.name ||
      !formData.email ||
      !formData.subject ||
      !formData.projectDetails
    ) {
      toast({
        title: "Missing Required Fields",
        description: "Please fill in all required fields before submitting.",
        variant: "destructive",
      })
      return
    }

    setIsSubmitting(true)
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      })

      if (!res.ok) throw new Error("Failed to submit")

      toast({
        title: "Message Sent Successfully",
        description:
          "Thank you for reaching out! Our team will get back to you within 24 hours.",
      })

      setFormData({
        name: "",
        company: "",
        email: "",
        phone: "",
        subject: "",
        projectDetails: "",
      })
    } catch {
      toast({
        title: "Submission Failed",
        description:
          "Something went wrong. Please try again or contact us directly.",
        variant: "destructive",
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* ───── HEADER / NAV ───── */}
      <header className="sticky top-0 z-50 border-b border-border/40 bg-background/80 backdrop-blur-lg">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          {/* Logo */}
          <a href="#" className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-600 text-white">
              <HardHat className="h-5 w-5" />
            </div>
            <span className="text-xl font-bold tracking-tight text-foreground">
              Dr. Clad
            </span>
          </a>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            <a
              href="#services"
              className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              Services
            </a>
            <a
              href="#contact-info"
              className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              Contact Info
            </a>
            <a
              href="#contact-form"
              className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              Get in Touch
            </a>
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <a href="tel:+61280001234">
              <Button variant="outline" size="sm" className="gap-2">
                <Phone className="h-4 w-4" />
                Call Us
              </Button>
            </a>
            <a href="#contact-form">
              <Button size="sm" className="gap-2 bg-amber-600 hover:bg-amber-700 text-white">
                Partner With Us
              </Button>
            </a>
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2 rounded-md hover:bg-accent"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <X className="h-5 w-5" />
            ) : (
              <Menu className="h-5 w-5" />
            )}
          </button>
        </div>

        {/* Mobile Nav */}
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="md:hidden border-t border-border/40 bg-background px-4 pb-4 pt-2"
          >
            <nav className="flex flex-col gap-3">
              <a
                href="#services"
                className="text-sm font-medium text-muted-foreground py-2 hover:text-foreground"
                onClick={() => setMobileMenuOpen(false)}
              >
                Services
              </a>
              <a
                href="#contact-info"
                className="text-sm font-medium text-muted-foreground py-2 hover:text-foreground"
                onClick={() => setMobileMenuOpen(false)}
              >
                Contact Info
              </a>
              <a
                href="#contact-form"
                className="text-sm font-medium text-muted-foreground py-2 hover:text-foreground"
                onClick={() => setMobileMenuOpen(false)}
              >
                Get in Touch
              </a>
              <div className="flex flex-col gap-2 pt-2">
                <a href="tel:+61280001234">
                  <Button variant="outline" size="sm" className="w-full gap-2">
                    <Phone className="h-4 w-4" />
                    Call Us
                  </Button>
                </a>
                <a href="#contact-form" onClick={() => setMobileMenuOpen(false)}>
                  <Button
                    size="sm"
                    className="w-full gap-2 bg-amber-600 hover:bg-amber-700 text-white"
                  >
                    Partner With Us
                  </Button>
                </a>
              </div>
            </nav>
          </motion.div>
        )}
      </header>

      <main className="flex-1">
        {/* ───── HERO SECTION ───── */}
        <section className="relative overflow-hidden">
          {/* Background image with overlay */}
          <div className="absolute inset-0">
            <img
              src="/hero-bg.png"
              alt="Construction site with plasterboard installation"
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/30" />
          </div>

          <div className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6 sm:py-32 lg:px-8 lg:py-40">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="max-w-2xl"
            >
              <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-amber-400/30 bg-amber-500/10 px-4 py-1.5 text-sm font-medium text-amber-300">
                <HardHat className="h-4 w-4" />
                Building Systems Contractor
              </div>

              <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl">
                Partner With{" "}
                <span className="text-amber-400">Dr. Clad</span>
              </h1>

              <p className="mt-6 text-lg leading-relaxed text-neutral-200 sm:text-xl">
                Looking for a reliable building systems contractor? Our
                experienced team is ready to support your next project with
                professional plasterboard, insulation, and AAC installation
                solutions.
              </p>

              <div className="mt-10 flex flex-col gap-4 sm:flex-row">
                <a href="#contact-form">
                  <Button
                    size="lg"
                    className="gap-2 bg-amber-600 hover:bg-amber-700 text-white font-semibold text-base px-8"
                  >
                    Get a Quote
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </a>
                <a href="#services">
                  <Button
                    size="lg"
                    variant="outline"
                    className="gap-2 border-white/20 text-white hover:bg-white/10 hover:text-white font-semibold text-base px-8"
                  >
                    Our Services
                  </Button>
                </a>
              </div>
            </motion.div>
          </div>
        </section>

        {/* ───── SERVICES SECTION ───── */}
        <section id="services" className="py-20 sm:py-28 bg-muted/30">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5 }}
              className="text-center"
            >
              <p className="text-sm font-semibold uppercase tracking-widest text-amber-600">
                What We Do
              </p>
              <h2 className="mt-3 text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
                Our Services
              </h2>
              <p className="mx-auto mt-4 max-w-2xl text-lg text-muted-foreground">
                Comprehensive building systems solutions delivered by a team you
                can trust.
              </p>
            </motion.div>

            <div className="mt-16 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {services.map((service, idx) => (
                <motion.div
                  key={service.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                >
                  <Card className="h-full border-border/50 bg-card shadow-sm hover:shadow-md transition-shadow duration-300">
                    <CardHeader className="pb-3">
                      <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
                        <service.icon className="h-6 w-6" />
                      </div>
                      <CardTitle className="text-xl font-semibold">
                        {service.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground leading-relaxed">
                        {service.description}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ───── WHY PARTNER SECTION ───── */}
        <section className="py-20 sm:py-28">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
              <motion.div
                initial={{ opacity: 0, x: -24 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5 }}
              >
                <p className="text-sm font-semibold uppercase tracking-widest text-amber-600">
                  Why Dr. Clad
                </p>
                <h2 className="mt-3 text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
                  Built on Trust, Delivered with Precision
                </h2>
                <p className="mt-5 text-lg text-muted-foreground leading-relaxed">
                  With years of industry experience, we combine technical
                  expertise with a commitment to safety and quality that sets us
                  apart.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 24 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5, delay: 0.15 }}
                className="space-y-5"
              >
                {[
                  "Fully licensed and insured building systems contractor",
                  "Experienced team with 500+ projects completed",
                  "Strict safety standards and site compliance",
                  "Competitive pricing with transparent quotations",
                  "On-time delivery with dedicated project management",
                ].map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
                    <span className="text-foreground leading-relaxed">
                      {item}
                    </span>
                  </div>
                ))}
              </motion.div>
            </div>
          </div>
        </section>

        {/* ───── CONTACT INFORMATION ───── */}
        <section id="contact-info" className="py-20 sm:py-28 bg-muted/30">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5 }}
              className="text-center"
            >
              <p className="text-sm font-semibold uppercase tracking-widest text-amber-600">
                Reach Out
              </p>
              <h2 className="mt-3 text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
                Contact Information
              </h2>
              <p className="mx-auto mt-4 max-w-2xl text-lg text-muted-foreground">
                Have questions or ready to start a project? Get in touch through
                any of the channels below.
              </p>
            </motion.div>

            <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {contactInfo.map((item, idx) => (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.4, delay: idx * 0.08 }}
                >
                  <a
                    href={item.href}
                    className="group block h-full rounded-xl border border-border/60 bg-card p-6 shadow-sm transition-all duration-300 hover:shadow-md hover:border-amber-200"
                  >
                    <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-lg bg-amber-50 text-amber-600 transition-colors group-hover:bg-amber-600 group-hover:text-white">
                      <item.icon className="h-5 w-5" />
                    </div>
                    <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                      {item.label}
                    </h3>
                    <p className="mt-2 text-foreground font-medium leading-relaxed">
                      {item.value}
                    </p>
                  </a>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ───── CONTACT FORM ───── */}
        <section id="contact-form" className="py-20 sm:py-28">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid gap-12 lg:grid-cols-5">
              {/* Left info column */}
              <motion.div
                initial={{ opacity: 0, x: -24 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5 }}
                className="lg:col-span-2"
              >
                <p className="text-sm font-semibold uppercase tracking-widest text-amber-600">
                  Get Started
                </p>
                <h2 className="mt-3 text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
                  Contact Form
                </h2>
                <p className="mt-5 text-lg text-muted-foreground leading-relaxed">
                  Fill in the form and our team will get back to you within 24
                  hours with a tailored response for your project needs.
                </p>

                <div className="mt-10 space-y-6">
                  <div className="flex items-start gap-3">
                    <div className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-50 text-amber-600">
                      <CheckCircle2 className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="font-semibold text-foreground">
                        Quick Response
                      </p>
                      <p className="text-sm text-muted-foreground">
                        We reply to all inquiries within 24 business hours.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-50 text-amber-600">
                      <CheckCircle2 className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="font-semibold text-foreground">
                        No Obligation
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Free initial consultation and project assessment.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-50 text-amber-600">
                      <CheckCircle2 className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="font-semibold text-foreground">
                        Expert Guidance
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Speak directly with our experienced project managers.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Form column */}
              <motion.div
                initial={{ opacity: 0, x: 24 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="lg:col-span-3"
              >
                <Card className="border-border/50 shadow-sm">
                  <CardContent className="p-6 sm:p-8">
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid gap-6 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor="name">
                            Name <span className="text-destructive">*</span>
                          </Label>
                          <Input
                            id="name"
                            name="name"
                            placeholder="Your full name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="company">Company</Label>
                          <Input
                            id="company"
                            name="company"
                            placeholder="Company name"
                            value={formData.company}
                            onChange={handleChange}
                          />
                        </div>
                      </div>

                      <div className="grid gap-6 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor="email">
                            Email <span className="text-destructive">*</span>
                          </Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            placeholder="you@company.com"
                            value={formData.email}
                            onChange={handleChange}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="phone">Phone</Label>
                          <Input
                            id="phone"
                            name="phone"
                            type="tel"
                            placeholder="+61 400 000 000"
                            value={formData.phone}
                            onChange={handleChange}
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="subject">
                          Subject <span className="text-destructive">*</span>
                        </Label>
                        <Select
                          value={formData.subject}
                          onValueChange={handleSubjectChange}
                        >
                          <SelectTrigger id="subject">
                            <SelectValue placeholder="Select a subject" />
                          </SelectTrigger>
                          <SelectContent>
                            {subjectOptions.map((opt) => (
                              <SelectItem key={opt} value={opt}>
                                {opt}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="projectDetails">
                          Project Details{" "}
                          <span className="text-destructive">*</span>
                        </Label>
                        <Textarea
                          id="projectDetails"
                          name="projectDetails"
                          placeholder="Tell us about your project — scope, timeline, location, and any specific requirements..."
                          rows={5}
                          value={formData.projectDetails}
                          onChange={handleChange}
                          required
                          className="resize-none"
                        />
                      </div>

                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full gap-2 bg-amber-600 hover:bg-amber-700 text-white font-semibold text-base sm:w-auto sm:px-10"
                      >
                        {isSubmitting ? (
                          <span className="flex items-center gap-2">
                            <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                            Sending...
                          </span>
                        ) : (
                          <>
                            <Send className="h-4 w-4" />
                            Send Message
                          </>
                        )}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ───── CTA BANNER ───── */}
        <section className="bg-amber-600">
          <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
            <div className="text-center">
              <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
                Let&apos;s build safer, smarter, and stronger projects together.
              </h2>
              <p className="mx-auto mt-4 max-w-xl text-lg text-amber-100">
                Whether you&apos;re planning a new build or need expert
                installation support, Dr. Clad is your trusted partner.
              </p>
              <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
                <a href="#contact-form">
                  <Button
                    size="lg"
                    className="gap-2 bg-white text-amber-700 hover:bg-amber-50 font-semibold text-base px-8"
                  >
                    Start a Conversation
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </a>
                <a href="tel:+61280001234">
                  <Button
                    size="lg"
                    variant="outline"
                    className="gap-2 border-white/30 text-white hover:bg-white/10 hover:text-white font-semibold text-base px-8"
                  >
                    <Phone className="h-4 w-4" />
                    +61 2 8000 1234
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* ───── FOOTER ───── */}
      <footer className="border-t border-border/40 bg-muted/20">
        <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center gap-6 sm:flex-row sm:justify-between">
            <div className="flex items-center gap-2.5">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-600 text-white">
                <HardHat className="h-4 w-4" />
              </div>
              <span className="text-lg font-bold tracking-tight text-foreground">
                Dr. Clad
              </span>
            </div>
            <nav className="flex flex-wrap justify-center gap-6">
              <a
                href="#services"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Services
              </a>
              <a
                href="#contact-info"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Contact
              </a>
              <a
                href="#contact-form"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Get a Quote
              </a>
            </nav>
          </div>
          <div className="mt-8 border-t border-border/40 pt-6 text-center">
            <p className="text-sm text-muted-foreground">
              &copy; {new Date().getFullYear()} Dr. Clad. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  )
}
